import { EditOutlined } from '@ant-design/icons'
import { enumTableTitleMegaMenu } from '@configs'
import { Skeleton, Tooltip, Typography, message } from 'antd'
import { t } from 'i18next'
import { useCallback, useEffect, useState } from 'react'
import { SharedTable } from 'src/common'
import { useMegaMenuActions, useMegaMenuSelectors } from 'src/hooks/useMegaMenu'
import { IMegaMenuItem } from 'src/interfaces/mega-menu'
import { MegaMenuDetailModal, IAddMegaMenuRequest } from './MegaMenuDetailModal'
import styled from 'styled-components'

const MegaMenuPage = () => {
  const [showModal, setShowModal] = useState(false)
  const [selectedMegaMenu, setSelectedMegaMenu] =
    useState<IMegaMenuItem | null>(null)

  // Redux hooks
  const { fetchMegaMenus, updateMegaMenuItem } = useMegaMenuActions()
  const { megaMenus, loading, updateLoading } = useMegaMenuSelectors()

  const getMegaMenus = useCallback(async () => {
    await fetchMegaMenus()
  }, [fetchMegaMenus])

  useEffect(() => {
    getMegaMenus()
  }, [getMegaMenus])

  const getListMegaMenuActionLoading = loading

  const columns = [
    {
      width: '5%',
      title: enumTableTitleMegaMenu.NO,
      dataIndex: 'index',
      key: 'index',
      ellipsis: true,
    },
    {
      width: '15%',
      title: enumTableTitleMegaMenu.PARENT_TITLE,
      dataIndex: 'parent',
      key: 'parent',
      render: (parent: IMegaMenuItem['parent']) => (
        <div className="flex items-center">
          {parent?.title || '-'}
        </div>
      ),
      ellipsis: true,
    },
    {
      width: '25%',
      title: enumTableTitleMegaMenu.TITLE,
      dataIndex: 'title',
      key: 'id',
      render: (e: string) => (
        <div className="flex items-center max-w-[460px]">
          <Typography.Text
            style={{ width: 400, whiteSpace: 'nowrap' }}
            ellipsis={true}
          >
            {e}
          </Typography.Text>
        </div>
      ),
      ellipsis: true,
    },
    {
      width: '20%',
      title: enumTableTitleMegaMenu.SUBTITLE,
      dataIndex: 'subTitle',
      key: 'subTitle',
      render: (e: string) => (
        <div className="flex items-center">
          <Typography.Text
            style={{ width: 300, whiteSpace: 'nowrap' }}
            ellipsis={true}
          >
            {e || '-'}
          </Typography.Text>
        </div>
      ),
      ellipsis: true,
    },
    {
      width: '10%',
      title: enumTableTitleMegaMenu.STATUS,
      dataIndex: 'isComing',
      key: 'status',
      render: (e: boolean) => (
        <div className="flex items-center capitalize">
          <span
            className={`px-2 py-1 rounded text-xs ${
              !e
                ? 'bg-green-100 text-green-800'
                : 'bg-yellow-100 text-yellow-800'
            }`}
          >
            {!e ? 'Active' : 'Coming Soon'}
          </span>
        </div>
      ),
      ellipsis: true,
    },
    {
      width: '15%',
      title: enumTableTitleMegaMenu.ACTION,
      key: 'action',
      dataIndex: 'action',
      render: (_: any, record: { id: any }) => (
        <div className="flex space-x-4 items-center">
          <Tooltip title={t('common:edit')}>
            <EditOutlined
              className="text-lg font-light mr-2.5 cursor-pointer text-[#184f64]"
              onClick={() => {
                const megaMenuItem = megaMenus.find(
                  (item) => item.id === record.id
                )
                setSelectedMegaMenu(megaMenuItem || null)
                setShowModal(true)
              }}
            />
          </Tooltip>
        </div>
      ),
      ellipsis: true,
    },
  ]

  const onCloseModal = () => {
    setShowModal(false)
    setSelectedMegaMenu(null)
  }

  const onSave = async (data: IAddMegaMenuRequest) => {
    if (!selectedMegaMenu) return

    try {
      await updateMegaMenuItem(selectedMegaMenu.id, data)
      message.success('Mega menu updated successfully')
      setShowModal(false)
      setSelectedMegaMenu(null)
      // Refresh the list
      getMegaMenus()
    } catch (error: any) {
      message.error(error.message || 'Failed to update mega menu')
    }
  }

  return (
    <MegaMenuManagementStyled>
      <div className="pb-[1.25rem]">
        {getListMegaMenuActionLoading ? (
          <Skeleton paragraph={{ rows: 4 }} />
        ) : (
          <SharedTable
            columns={columns as any}
            dataSource={megaMenus.map((item, index) => ({
              index: index + 1,
              key: item.id,
              id: item.id,
              title: item.title,
              subTitle: item.subTitle,
              parent: item.parent,
              isComing: item.isComing,
            }))}
            sticky
          />
        )}
      </div>
      {showModal && (
        <MegaMenuDetailModal
          open={showModal}
          onClose={onCloseModal}
          isLoading={updateLoading}
          changeLoading={() => {}} // Not needed with Redux
          onSave={onSave}
          item={selectedMegaMenu}
        />
      )}
    </MegaMenuManagementStyled>
  )
}

export default MegaMenuPage

const MegaMenuManagementStyled = styled('div')(() => {
  return {
    padding: '20px',
    borderRadius: '8px',
    backgroundColor: '#fff',
    '& .ant-table-wrapper': {
      margin: 0,
      '& .ant-table-content': {
        marginRight: 0,
      },
    },
  }
})
